import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Search, MapPin, Navigation, Calendar, LocateFixed, X, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const createMarkerIcon = (isActive = false) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="marker-icon ${isActive ? 'active' : ''}" style="
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${isActive ? '#aec595' : '#556f20'};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 2px 8px rgba(85, 111, 32, 0.4);
        transition: all 0.2s ease;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="transform: rotate(45deg);">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// User location marker
const userLocationIcon = L.divIcon({
  className: 'custom-marker',
  html: `
    <div style="
      width: 20px;
      height: 20px;
      background: #4285f4;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(66, 133, 244, 0.5);
    "></div>
  `,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Map controller component for flying to locations
function MapController({ center, zoom, selectedOffice }) {
  const map = useMap();
  
  useEffect(() => {
    if (center && zoom) {
      map.flyTo(center, zoom, { duration: 1 });
    }
  }, [center, zoom, map]);

  useEffect(() => {
    if (selectedOffice) {
      map.flyTo(
        [selectedOffice.coordinates.lat, selectedOffice.coordinates.lng],
        13,
        { duration: 0.8 }
      );
    }
  }, [selectedOffice, map]);

  return null;
}

// Radius filter pills - 5, 10, 15 miles with 15 as default
const RADIUS_OPTIONS = [
  { value: 5, label: '5 mi' },
  { value: 10, label: '10 mi' },
  { value: 15, label: '15 mi' },
];

// Abington office ID - always shows in search results
const ABINGTON_OFFICE_ID = '1';

export default function LocatorWidget() {
  const [offices, setOffices] = useState([]);
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [selectedRadius, setSelectedRadius] = useState(15); // Default to 15 miles
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [mapCenter, setMapCenter] = useState([40.2, -75.5]); // PA center
  const [mapZoom, setMapZoom] = useState(8);
  const [isSearching, setIsSearching] = useState(false);
  const [mobileListOpen, setMobileListOpen] = useState(false);
  const listRef = useRef(null);

  // Fetch offices
  const fetchOffices = useCallback(async (lat = null, lng = null, radius = null) => {
    try {
      let url = `${API}/offices`;
      const params = new URLSearchParams();
      
      if (lat !== null && lng !== null) {
        params.append('lat', lat);
        params.append('lng', lng);
      }
      if (radius !== null) {
        params.append('radius', radius);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url);
      let data = await response.json();
      
      // Always include Abington office and put it FIRST
      if (lat !== null && lng !== null) {
        // Remove Abington from current position if exists
        const abingtonIndex = data.findIndex(o => o.id === ABINGTON_OFFICE_ID);
        let abingtonOffice = null;
        
        if (abingtonIndex >= 0) {
          abingtonOffice = data[abingtonIndex];
          data.splice(abingtonIndex, 1);
        } else {
          // Fetch Abington office separately if not in filtered results
          const abingtonResponse = await fetch(`${API}/offices/${ABINGTON_OFFICE_ID}?lat=${lat}&lng=${lng}`);
          if (abingtonResponse.ok) {
            abingtonOffice = await abingtonResponse.json();
          }
        }
        
        // Put Abington first
        if (abingtonOffice) {
          data = [abingtonOffice, ...data];
        }
      }
      
      setOffices(data);
      setFilteredOffices(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching offices:', error);
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchOffices();
  }, [fetchOffices]);

  // Handle search
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await fetch(`${API}/geocode?address=${encodeURIComponent(searchQuery)}`);
      if (response.ok) {
        const data = await response.json();
        setUserLocation({ lat: data.lat, lng: data.lng });
        setMapCenter([data.lat, data.lng]);
        setMapZoom(11);
        await fetchOffices(data.lat, data.lng, selectedRadius);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    setIsSearching(false);
  };

  // Handle current location
  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      setIsSearching(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setMapCenter([latitude, longitude]);
          setMapZoom(11);
          setSearchQuery('Current Location');
          await fetchOffices(latitude, longitude, selectedRadius);
          setIsSearching(false);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setIsSearching(false);
        }
      );
    }
  };

  // Handle radius change
  const handleRadiusChange = async (radius) => {
    setSelectedRadius(radius);
    if (userLocation) {
      await fetchOffices(userLocation.lat, userLocation.lng, radius);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setUserLocation(null);
    setSelectedRadius(null);
    setMapCenter([40.2, -75.5]);
    setMapZoom(8);
    fetchOffices();
  };

  // Handle office selection
  const handleOfficeSelect = (office) => {
    setSelectedOffice(office);
    setMobileListOpen(false);
  };

  // Get directions URL
  const getDirectionsUrl = (office) => {
    const address = encodeURIComponent(
      `${office.address}, ${office.city}, ${office.state} ${office.zip_code}`
    );
    return `https://www.google.com/maps/dir/?api=1&destination=${address}`;
  };

  // Scroll to office in list
  const scrollToOffice = (officeId) => {
    const element = document.getElementById(`office-${officeId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="w-full h-full flex flex-col md:flex-row overflow-hidden bg-white" data-testid="locator-widget">
      {/* Sidebar / List Panel */}
      <div 
        className={`
          w-full md:w-[400px] lg:w-[450px] 
          ${mobileListOpen ? 'h-[70vh]' : 'h-auto'} md:h-full 
          flex flex-col bg-white z-20 
          md:shadow-none border-r border-slate-200/50
          transition-all duration-300 ease-out
          md:relative fixed bottom-0 left-0 right-0 md:bottom-auto
          rounded-t-2xl md:rounded-none shadow-[0_-4px_20px_rgba(0,0,0,0.1)] md:shadow-none
        `}
        data-testid="sidebar-panel"
      >
        {/* Mobile drag handle */}
        <div 
          className="md:hidden flex justify-center py-2 cursor-pointer"
          onClick={() => setMobileListOpen(!mobileListOpen)}
        >
          <div className="drawer-handle"></div>
        </div>

        {/* Header */}
        <div className="p-4 md:p-5 border-b border-slate-100">
          <h1 className="text-xl md:text-2xl font-bold text-[#1f291f] font-['Manrope'] mb-4" data-testid="widget-title">
            Find a Location
          </h1>
          
          {/* Search Bar */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Enter address or zip code"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 pr-10 h-11 rounded-full border-slate-200 focus-visible:ring-[#aec595] bg-white"
              data-testid="search-input"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="clear-search-btn"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Search Actions */}
          <div className="flex gap-2 mb-4">
            <Button
              onClick={handleSearch}
              disabled={isSearching || !searchQuery.trim()}
              className="flex-1 bg-[#556f20] hover:bg-[#44591a] text-white rounded-lg h-10 text-sm font-medium"
              data-testid="search-btn"
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
            <Button
              onClick={handleUseCurrentLocation}
              variant="outline"
              className="flex items-center gap-2 border-[#aec595] text-[#556f20] hover:bg-[#aec595]/10 rounded-lg h-10 px-3"
              data-testid="use-location-btn"
            >
              <LocateFixed className="h-4 w-4" />
              <span className="hidden sm:inline">Use My Location</span>
            </Button>
          </div>

          {/* Radius Filter */}
          {userLocation && (
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-testid="radius-filter">
              {RADIUS_OPTIONS.map((option) => (
                <button
                  key={option.label}
                  onClick={() => handleRadiusChange(option.value)}
                  className={`
                    px-4 py-1.5 rounded-full text-sm font-medium transition-colors border whitespace-nowrap
                    ${selectedRadius === option.value 
                      ? 'bg-[#556f20] text-white border-[#556f20]' 
                      : 'bg-white text-[#5c6b5c] border-slate-200 hover:border-[#aec595]/50 hover:text-[#556f20]'
                    }
                  `}
                  data-testid={`radius-${option.value || 'all'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Mobile toggle button */}
        <button
          className="md:hidden flex items-center justify-center gap-2 py-3 text-[#556f20] font-medium text-sm border-b border-slate-100"
          onClick={() => setMobileListOpen(!mobileListOpen)}
          data-testid="mobile-toggle-list"
        >
          {mobileListOpen ? (
            <>
              <ChevronDown className="h-4 w-4" />
              Hide Locations ({filteredOffices.length})
            </>
          ) : (
            <>
              <ChevronUp className="h-4 w-4" />
              Show Locations ({filteredOffices.length})
            </>
          )}
        </button>

        {/* Location List */}
        <ScrollArea className={`flex-1 ${mobileListOpen ? 'block' : 'hidden'} md:block`}>
          <div className="p-4 space-y-3" data-testid="location-list">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#aec595] border-t-transparent"></div>
              </div>
            ) : filteredOffices.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <MapPin className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No locations found</p>
                <p className="text-sm mt-1">Try expanding your search radius</p>
              </div>
            ) : (
              filteredOffices.map((office, index) => {
                const isAbington = office.id === ABINGTON_OFFICE_ID;
                
                return (
                  <div
                    key={office.id}
                    id={`office-${office.id}`}
                    className={`
                      group relative flex flex-col gap-3 p-4 rounded-xl border 
                      transition-all duration-300 cursor-pointer location-card-animate
                      ${selectedOffice?.id === office.id 
                        ? 'border-[#aec595] ring-1 ring-[#aec595]/20 bg-[#fcfdfb] shadow-md' 
                        : 'border-slate-200/60 bg-white hover:border-[#aec595]/50 hover:shadow-md'
                      }
                    `}
                    style={{ animationDelay: `${index * 50}ms` }}
                    onClick={() => handleOfficeSelect(office)}
                    data-testid={`office-card-${office.id}`}
                  >
                    {/* Distance Badge */}
                    {office.distance !== null && office.distance !== undefined && (
                      <span className="inline-flex items-center self-start px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#aec595]/15 text-[#556f20]">
                        {office.distance} miles away
                      </span>
                    )}

                    {/* Office Name */}
                    <h3 className="text-base font-semibold text-[#1f291f] font-['Manrope'] leading-tight">
                      {office.name}
                    </h3>

                    {/* Subtitle for Abington */}
                    {isAbington && (
                      <p className="text-sm text-[#556f20] font-medium -mt-1">
                        In office appointments only
                      </p>
                    )}

                    {/* Address - Only show for Abington */}
                    {isAbington && (
                      <p className="text-sm text-[#5c6b5c] leading-relaxed">
                        {office.address}<br />
                        {office.city}, {office.state} {office.zip_code}
                      </p>
                    )}

                    {/* Service Radius Info - Only for non-Abington */}
                    {!isAbington && (
                      <p className="text-xs text-[#889988]">
                        Service area: {office.radius_miles} mile radius
                      </p>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-1">
                      <a
                        href={office.booking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1"
                      >
                        <Button 
                          className="w-full bg-[#556f20] hover:bg-[#44591a] text-white rounded-lg h-9 text-sm font-medium gap-2"
                          data-testid={`book-btn-${office.id}`}
                        >
                          <Calendar className="h-4 w-4" />
                          Book Appointment
                        </Button>
                      </a>
                      {/* Directions button - Only show for Abington */}
                      {isAbington && (
                        <a
                          href={getDirectionsUrl(office)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button 
                            variant="outline"
                            className="border-[#aec595] text-[#556f20] hover:bg-[#aec595]/10 rounded-lg h-9 px-3"
                            data-testid={`directions-btn-${office.id}`}
                          >
                            <Navigation className="h-4 w-4" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        {/* Results count */}
        <div className="hidden md:block p-3 border-t border-slate-100 text-center text-sm text-[#889988]">
          {filteredOffices.length} location{filteredOffices.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Map Area */}
      <div className="flex-1 h-[50vh] md:h-full relative z-10" data-testid="map-container">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          
          <MapController 
            center={mapCenter} 
            zoom={mapZoom} 
            selectedOffice={selectedOffice}
          />

          {/* User location marker */}
          {userLocation && (
            <Marker
              position={[userLocation.lat, userLocation.lng]}
              icon={userLocationIcon}
            >
              <Popup>Your location</Popup>
            </Marker>
          )}

          {/* Office markers and radius circles */}
          {filteredOffices.map((office) => {
            const isAbington = office.id === ABINGTON_OFFICE_ID;
            
            return (
              <React.Fragment key={office.id}>
                {/* Service radius circle - only for non-Abington */}
                {!isAbington && (
                  <Circle
                    center={[office.coordinates.lat, office.coordinates.lng]}
                    radius={office.radius_miles * 1609.34} // Convert miles to meters
                    pathOptions={{
                      color: '#aec595',
                      fillColor: '#aec595',
                      fillOpacity: 0.12,
                      weight: 1.5,
                    }}
                    className="radius-circle"
                  />
                )}
                
                {/* Office marker */}
                <Marker
                  position={[office.coordinates.lat, office.coordinates.lng]}
                  icon={createMarkerIcon(selectedOffice?.id === office.id)}
                  eventHandlers={{
                    click: () => {
                      handleOfficeSelect(office);
                      scrollToOffice(office.id);
                    },
                  }}
                >
                  <Popup>
                    <div className="p-1">
                      <h3 className="font-semibold text-[#1f291f] text-sm mb-1">{office.name}</h3>
                      {isAbington ? (
                        <>
                          <p className="text-xs text-[#556f20] font-medium mb-1">In office appointments only</p>
                          <p className="text-xs text-[#5c6b5c] mb-2">
                            {office.address}, {office.city}, {office.state}
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-[#5c6b5c] mb-2">
                          Service area: {office.radius_miles} mi
                        </p>
                      )}
                      <div className="flex gap-1">
                        <a
                          href={office.booking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'white', textDecoration: 'none' }}
                          className="text-xs bg-[#556f20] px-3 py-1.5 rounded hover:bg-[#44591a]"
                        >
                          Book
                        </a>
                        {isAbington && (
                          <a
                            href={getDirectionsUrl(office)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'black', textDecoration: 'none' }}
                            className="text-xs border border-[#aec595] px-3 py-1.5 rounded hover:bg-[#aec595]/10"
                          >
                            Directions
                          </a>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              </React.Fragment>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
